import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import AlertBanner from '../../../components/common/AlertBanner/AlertBanner';
import Button from '../../../components/common/Button/Button';
import ConfirmModal from '../../../components/common/ConfirmModal/ConfirmModal';
import DecoratedHeader from '../../../components/common/DecoratedHeader/DecoratedHeader';
import Loading from '../../../components/common/Loading/Loading';
import Logo from '../../../components/common/Logo/Logo';
import { AppFormInput } from '../../../components/common/appForms';
import { appOnboardingText } from '../../../components/common/appUi';
import PublicFormAnsweredState from '../../../components/common/PublicFormAnsweredState/PublicFormAnsweredState';
import { ParQService, PublicStudentContextService } from '../../../services';
import type { ParQFormData } from '../../../types';
import { STUDENT_TOKEN_QUERY_PARAM } from '../../../utils/studentPublicLinks';
import { PARQ_QUESTIONS } from '../../../constants/parqQuestions';
import ParQStep from '../../Students/StudentRegister/steps/ParQStep';
import {
  buildPublicParqPayload,
  createEmptyPublicParqLockedFields,
  createInitialPublicParqForm,
  createInitialPublicParqIdentification,
  mapStudentToPublicParqIdentification,
  resolvePublicParqLockedFields,
  focusNearestPublicParqError,
  sanitizePublicParqForm,
  validatePublicParqForm,
} from './publicParq.utils';
import type { PublicParqIdentificationState, PublicParqLockedFields } from './publicParq.utils';
import formStyles from './ParQRegister.module.css';
import styles from '../../Fup/Fup.module.css';

type FeedbackState = {
  message: string;
  variant: 'error' | 'success';
} | null;

const getErrorMessage = (error: unknown, fallbackMessage: string): string => {
  if (error instanceof Error && error.message) {
    return error.message;
  }

  return fallbackMessage;
};

const scrollToPageTop = () => {
  window.scrollTo({ top: 0, behavior: 'smooth' });
};

const ParQRegister: React.FC = () => {
  const [searchParams] = useSearchParams();
  const studentToken = searchParams.get(STUDENT_TOKEN_QUERY_PARAM)?.trim() || '';
  const [identification, setIdentification] = useState<PublicParqIdentificationState>(
    createInitialPublicParqIdentification,
  );
  const [parq, setParQ] = useState<ParQFormData>(createInitialPublicParqForm);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [feedback, setFeedback] = useState<FeedbackState>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [isLoadingStudent, setIsLoadingStudent] = useState(Boolean(studentToken));
  const [loadStudentError, setLoadStudentError] = useState<string | null>(null);
  const [lockedFields, setLockedFields] = useState<PublicParqLockedFields>(
    createEmptyPublicParqLockedFields,
  );
  const [studentId, setStudentId] = useState<number | null>(null);
  const [isAlreadyAnswered, setIsAlreadyAnswered] = useState(false);
  const [answeredAt, setAnsweredAt] = useState<string | null>(null);

  const handleIdentificationChange = <K extends keyof PublicParqIdentificationState>(
    field: K,
    value: PublicParqIdentificationState[K],
  ) => {
    if (lockedFields[field]) {
      return;
    }

    setIdentification((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: '' }));
    setFeedback(null);
  };

  const handleParQFieldChange = useCallback(<K extends keyof ParQFormData>(
    field: K,
    value: ParQFormData[K],
  ) => {
    setParQ((prev) => sanitizePublicParqForm({ ...prev, [field]: value }));
    setErrors((prev) => {
      const nextErrors = { ...prev, [field]: '' };

      PARQ_QUESTIONS.forEach((question) => {
        if (question.detailFormField && field === question.field && value !== true) {
          nextErrors[question.detailFormField] = '';
        }
      });

      return nextErrors;
    });
    setFeedback(null);
  }, []);

  useEffect(() => {
    if (!studentToken) {
      setIsLoadingStudent(false);
      setLoadStudentError('Este formulário só pode ser acessado pelo link enviado. Solicite um novo link à equipe.');
      setLockedFields(createEmptyPublicParqLockedFields());
      setStudentId(null);
      setIsAlreadyAnswered(false);
      setAnsweredAt(null);
      return;
    }

    let isMounted = true;

    const loadStudentData = async () => {
      setIsLoadingStudent(true);
      setLoadStudentError(null);

      try {
        const context = await PublicStudentContextService.resolve(studentToken);

        if (!isMounted) {
          return;
        }

        setIdentification(mapStudentToPublicParqIdentification(context.student));
        setParQ(createInitialPublicParqForm());
        setLockedFields(resolvePublicParqLockedFields(context.student));
        setStudentId(context.student.id);
        setIsAlreadyAnswered(!context.formsStatus.can_submit_parq);
        setAnsweredAt(context.formsStatus.last_complete_parq_at);
        setErrors({});
        setFeedback(null);
      } catch (error) {
        if (!isMounted) {
          return;
        }

        setLoadStudentError(
          error instanceof Error ? error.message : 'Não foi possível carregar os dados da aluna.',
        );
      } finally {
        if (isMounted) {
          setIsLoadingStudent(false);
        }
      }
    };

    void loadStudentData();

    return () => {
      isMounted = false;
    };
  }, [studentToken]);

  const handleOpenConfirmModal = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!studentToken) {
      setFeedback({
        message: 'Este formulário só pode ser acessado pelo link enviado. Solicite um novo link à equipe.',
        variant: 'error',
      });
      return;
    }

    if (isAlreadyAnswered) {
      return;
    }

    const nextErrors = validatePublicParqForm(identification, parq, true);
    setErrors(nextErrors);

    if (Object.keys(nextErrors).length > 0) {
      window.setTimeout(() => {
        focusNearestPublicParqError(nextErrors);
      }, 0);
      return;
    }

    setShowConfirmModal(true);
  };

  const handleConfirmSubmit = async () => {
    if (!studentToken) {
      return;
    }

    setIsSubmitting(true);
    setFeedback(null);

    try {
      await ParQService.submitRegistration(buildPublicParqPayload(identification, parq, {
        studentId: studentId ?? undefined,
        studentToken,
      }));
      setShowConfirmModal(false);
      setIsAlreadyAnswered(true);
      setAnsweredAt(new Date().toISOString());
      setFeedback(null);
      scrollToPageTop();
    } catch (error: unknown) {
      setFeedback({
        message: getErrorMessage(error, 'Não foi possível enviar o Par-Q agora.'),
        variant: 'error',
      });
      scrollToPageTop();
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderIdentificationSection = () => (
    <div className={formStyles.nameEmailFields}>
      <AppFormInput
        id="public-parq-student-name"
        label="Nome completo"
        value={identification.studentName}
        onChange={(event) => handleIdentificationChange('studentName', event.target.value)}
        error={errors.studentName}
        placeholder="Digite seu nome"
        disabled={isSubmitting}
        readOnly={lockedFields.studentName}
        required
      />

      <AppFormInput
        id="public-parq-email"
        label="E-mail"
        type="email"
        value={identification.email}
        onChange={(event) => handleIdentificationChange('email', event.target.value)}
        error={errors.email}
        placeholder="Digite seu e-mail"
        disabled={isSubmitting}
        readOnly={lockedFields.email}
        required
      />
    </div>
  );

  if (isLoadingStudent) {
    return <Loading fullscreen />;
  }

  return (
    <div className={styles.page}>
      <div className={styles.shell}>
        <div className={styles.header}>
          <Logo />
        </div>

        <section className={styles.panelCard}>
          <DecoratedHeader
            title={'Questionário de Prontidão\npara Atividade Física (PAR-Q)'}
            preserveLineBreaks
          />
          <p className={`${appOnboardingText.intro} ${formStyles.intro}`}>
            Este questionário tem o objetivo de identificar a necessidade de
            avaliação por um médico antes do início da atividade física.
            Caso você responda <span className={appOnboardingText.bold}>SIM</span> a uma ou mais perguntas,
            converse com seu médico <span className={appOnboardingText.bold}>ANTES</span> de aumentar seu nível
            atual de atividade física. Mencione este questionário e as perguntas
            às quais você respondeu <span className={appOnboardingText.bold}>SIM</span>.
          </p>
          <p className={`${appOnboardingText.intro} ${formStyles.intro}`}>
            Por gentileza, assinale <span className={appOnboardingText.bold}>SIM</span> ou <span className={appOnboardingText.bold}>NÃO</span>
            {' '}de acordo com as perguntas a seguir:
          </p>

          {loadStudentError && (
            <AlertBanner
              message={loadStudentError}
              variant="error"
              className={styles.feedbackAlert}
            />
          )}

          {feedback && (
            <AlertBanner
              message={feedback.message}
              variant={feedback.variant}
              className={styles.feedbackAlert}
            />
          )}

          {!loadStudentError && isAlreadyAnswered && (
            <PublicFormAnsweredState
              title="Seu PAR-Q já foi respondido."
              thankYouMessage="Obrigada por compartilhar suas informações conosco. Nossa equipe já recebeu seus dados."
              answeredAt={answeredAt}
            />
          )}

          {!loadStudentError && !isAlreadyAnswered && studentToken && (
            <form className={styles.formWrapper} onSubmit={handleOpenConfirmModal} noValidate>
              <div className={formStyles.panelContent}>
                {renderIdentificationSection()}
                <ParQStep
                  data={parq}
                  errors={errors}
                  onFieldChange={handleParQFieldChange}
                  disabled={isSubmitting}
                  useAppInputs
                />
              </div>

              <div className={styles.footerActions}>
                <Button
                  type="submit"
                  variant="primary"
                  size="app"
                  fullWidth
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Enviando...' : 'Enviar Par-Q'}
                </Button>
              </div>
            </form>
          )}
        </section>
      </div>

      <ConfirmModal
        open={showConfirmModal}
        title="Confirmar envio"
        confirmLabel="Confirmar envio"
        cancelLabel="Cancelar"
        variant="warning"
        loading={isSubmitting}
        onCancel={() => setShowConfirmModal(false)}
        onConfirm={handleConfirmSubmit}
      >
        <p>Após enviar seu Par-Q, confirme que as informações estão corretas.</p>
        <p>Deseja realmente enviar o questionário?</p>
      </ConfirmModal>
    </div>
  );
};

export default ParQRegister;
